<script setup lang="ts">
import { ref, onMounted, watch, defineAsyncComponent, createVNode, render } from 'vue'
import { driver } from 'driver.js'
import { useScreenSize } from '../../../utils/useScreenSize';
import { Permission } from '../../../types/auth';
import { getMenuFromPath, getPermissionList } from '../../../utils/CommonFunctions';
import MessageBox from '../../../services/MessageBox';
import DataTable from '../../table/DataTable.vue';
import NoPermissions from './NoPermissions.vue';
import Buttons from '../form/Buttons.vue';
import ListDetailPanel from './ListDetailPanel.vue';
import { FilterField, Params, TableAction, TableField, Tabs } from '../../table/table';
import IconComponent from './IconComponent.vue';
import { tokenManager } from '../../../api/utils/tokenManager';

const props = withDefaults(defineProps<{
  endPoint?: string;
  customUrl?: string;
  title: string | null;
  fields: TableField[];
  actions?: TableAction[];
  filterFields?: FilterField[];
  tabList: Tabs[];
  refreshData: boolean;
  showForm: boolean
  data?: any | [],
  remote?: boolean,
  showHeader?: boolean,
  showQuickTour?: boolean,
  showQuickView?: boolean,
  showShareActions?: boolean
  allowDisable?: boolean,
  showActions?: boolean,
  showPagination?: boolean,
  allowEdit?: boolean
  uid?: string | number
  titleField?: string
  showChildRow?: boolean
  childRowFields?: TableField[];
  childRowTitle?: string
  childRowTableWidth?: string
  childRowDataField?: string
  tableWidth?: string
  ignoreMaxHeight?: boolean
  showTitle?: boolean
  requireParams?: boolean
  params?: Params[]
  showNextButton?: boolean
  ignorePermissions?: boolean
  perm?: Permission[]
  hideTable?: boolean
  pageSize?: number
  page?: number
  ignoreZero?: boolean
}>(), {
  remote: true,
  showHeader: true,
  showQuickTour: true,
  showQuickView: true,
  allowDisable: true,
  showPagination: true,
  showActions: true,
  showShareActions: false,
  allowEdit: true,
  showChildRow: false,
  showTitle: true,
  pageSize: 25,
  page: 0,
  ignoreZero: false,
})

const { isMobile } = useScreenSize();

const emit = defineEmits<{
  (e: "custom", action: any, data: any): void;
  (e: "mode", action: any, item: any): void;
  (e: "data", data: any): void;
  (e: "show"): void;
  (e: "next"): void;
  (e: "edit"): void;
  (e: "close"): void;
  (e: "startTour"): void;
  (e: 'lists', data: any) : void
  (e: 'dataLoaded', data: any[]) : void
  (e: 'permissions', data: Permission[]) : void
}>();

watch(
  () => props.page,
  (_newValue) => {
    console.log('page prop changed lsit temp', props.page);
  },
  { immediate: true }
);

const selectedData = ref<any | null>(null)
const displayFormView = ref(false)
const totalRecords = ref(0)
const showDetailPanel = ref(false)
const reloadTab = ref(false)
const reloadData = ref(false)

const permissions = ref<Permission[]>();
const noPermission = ref(true)

const handleTotalRecords = (records: number) => {
  totalRecords.value = records
}

const handleCreate = () => {
  selectedData.value = null
  emit('data', selectedData.value)
  emit('mode', 'create', null)
  displayFormView.value = true
}

const handleTableAction = (action: string, item: any) => {
  selectedData.value = item
  emit('data', item)    
  switch (action) {
    case 'view':
      displayFormView.value = true
      emit('mode', action, item)
      emit('show')
      emit('custom', action, item)
      break
    case 'edit':
      displayFormView.value = true
      emit('mode', action, item)
      emit('show')
      break
    case 'showQuickView':
      reloadTab.value = !reloadTab.value
      showDetailPanel.value = true
      break
    case 'toggleStatus':
      item.active = item.active === '1' ? '0' : '1'
      break
    default:
      emit('custom', action, item)
  }

}

// Initialize driver.js tour
const driverObj = driver({
  showProgress: true,
  steps: [
  {
      element: '#page-list-header',
      popover: {
        title: props.title + ' Management',
        description: 'Welcome to the ' + props.title + ' management section! Here you can view and manage all your ' + props.title + 's.',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '#table-search',
      popover: {
        title: 'Search',
        description: 'Use these field to quickly find records. You can search by name, email, or phone number.',
        side: 'bottom'
      }
    },
    {
      element: '#table-report',
      popover: {
        title: 'Quick Reports',
        description: 'Use this dropdown to Access Quick Reports. You can print or export to common formats',
        side: 'bottom'
      }
    },
    {
      element: '#table-filter',
      popover: {
        title: 'Filter',
        description: 'Use this function to filters and narrow down the results.',
        side: 'bottom'
      }
    },
    {
      element: '#table-create',
      popover: {
        title: 'Create Record',
        description: 'Use this button to create a new record',
        side: 'bottom'
      }
    },
    {
      element: '#table-headers',
      popover: {
        title: 'Table Header',
        description: 'Display the Column Headers',
        side: 'bottom'
      }
    },
    {
      element: '#table-header-sort',
      popover: {
        title: 'Column Sorting',
        description: 'Indicated the column is sortable. <br />↕ means the column is sortable but not sorted. <br />↑ means the column is sorted in ascending order <br />↓ means the column is sorted in descending order',
        side: 'bottom'
      }
    },
    {
      element: '#table-records',
      popover: {
        title: 'Records',
        description: 'View all your records in this table. Click on any record to see their details in the side panel.',
        side: 'left'
      }
    },
    {
      element: '#table-actions',
      popover: {
        title: 'Quick Actions',
        description: 'Perform common actions like viewing details, editing information, or changing record status.',
        side: 'left'
      }
    },
    {
      element: '#table-action-show-quick-view',
      popover: {
        title: 'Quick View',
        description: 'Display the Exchange Rates on the right side',
        side: 'left'
      }
    },
    {
      element: '#table-action-view',
      popover: {
        title: 'View',
        description: 'Display the Record Details',
        side: 'left'
      }
    },
    {
      element: '#table-action-edit',
      popover: {
        title: 'Edit',
        description: 'Edi the record details',
        side: 'left'
      }
    },
    {
      element: '#table-action-exchange-rates',
      popover: {
        title: 'Exchange Rates',
        description: 'Add/Update Exchange Rates',
        side: 'left'
      }
    },
    {
      element: '#table-action-toggle-status',
      popover: {
        title: 'Quick Actions',
        description: 'Enable/Disable record',
        side: 'left'
      }
    },
    {
      element: '#details-panel',
      popover: {
        title: 'Additional Details',
        description: 'View detailed information for the record instantly.',
        side: 'left'
      }
    }
  ]
})


watch(() => props.perm, () => {
  if (props.perm) {
    permissions.value = props.perm
  }
})
const headerIcon = ref('')
onMounted(() => {
  permissions.value =  props.perm || getPermissionList(props.endPoint?.substring(1) || '')
  if (permissions.value && permissions.value.length <=0) {
    if (tokenManager.getUser()?.email == 'shuhood@gmail.com' || props.ignorePermissions) {
      noPermission.value = false
    } else  {
      MessageBox.show('You do not have permission to use this feature', 'warning')
    }
  } else {
    const menu = getMenuFromPath(props.endPoint?.substring(1) || '')
    if (menu)
      headerIcon.value = menu.icon_css
    emit('permissions', permissions.value)
    noPermission.value = false
    const hasSeenTour = localStorage.getItem('quickTour' + props.title + 'Form')
    if (!hasSeenTour) {
      driverObj.drive()
      localStorage.setItem('quickTour' + props.title + 'Form', 'true')
    }
  }
})

const handleLists = (data: any) => {
  emit('lists', data)
}
const startTour = () => {
  driverObj.drive()
}

watch(() => props.showForm, () => {
  displayFormView.value = false
})

watch(() => props.refreshData, () => {
  reloadData.value = !reloadData.value
})

const viewDocument = (data: any, title: string, path: string, manual: boolean = false) => {
  const comp_path = path.replace("/clients", "").replace(/-/g, "");
  const comp =
    (comp_path[0] != "/" ? "/" : '') +
    (comp_path?.slice(-1) == "/" ? "" : comp_path + "/") +
    title.replace(/ /g, "") +
    (title.slice(-1) == "s" ? "" : "s") +
    "Form.vue";
    
  const modalComponent = "../../../features" + comp;
  const FormModal = defineAsyncComponent(() =>
    import(/* @vite-ignore */ modalComponent).catch(() => {
      MessageBox.show("Could not load form", "error");
      return null;
    })
  );
  const container = document.createElement("div");
  document.body.appendChild(container);

  let perm: Permission[] = getPermissionList(path.substring(0, 1) == '/' ? path.substring(1) : path);
  const vnode = createVNode(FormModal, {
    mode: manual ? 'create' : "view",
    permissions: perm,
    title: title,
    endPoint: 'clients/' + path,
    //readonly: !manual,
    data: data,
    listCreateName: "",
    //copyItems: manual,
    onClose: () => {
      render(null, container);
      container.remove();
    },
  });

  // Render the modal
  render(vnode, container);
};

defineExpose({ viewDocument });


const handleDataLoaded = (data: any[]) => {
  emit('dataLoaded', data)
}

</script>

<template>
  <!--<div class="flex max-h-[calc(100vh-7rem)]">-->
  <div :class="{'md:max-h-[calc(100vh-7rem)]' : !ignoreMaxHeight}">
    <div class="flex-1 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6 overflow-y-auto">
      <div class="flex justify-between items-center mb-6" id="page-list-header" v-if="showTitle">
        <div class="flex items-center gap-3" id="page-list-header-title">
          <h2 class="text-2xl font-semibold text-gray-800 dark:text-white flex"><IconComponent :name="headerIcon" class="mr-1 mt-1" />{{ title}} <span v-if="props.showPagination && !isMobile">&nbsp; ({{ totalRecords }} Records)</span></h2>
          <slot name="header-left"></slot>
        </div>
        <div id="page-list-header-actions" class="flex gap-3" v-if="!noPermission">
          <Buttons caption="Quick Tour" v-if="showQuickTour && showHeader" icon="BadgeHelp"  class="hidden md:flex" iconColor="red" @click="startTour" id="quick-tour"/>
          <Buttons caption="Next" icon="CircleArrowRight" v-if="showNextButton" @click="$emit('next')" id="next-button"/>
          <slot name="header-actions"></slot>
        </div>
      </div>

      <NoPermissions v-if="noPermission" :modal="false"/>

      <slot name="pre-content"></slot>

      <DataTable
      :fields="fields"
      v-if="!noPermission && !hideTable"
      :show-filter="filterFields && filterFields.length > 0"
      :filterFields="filterFields"
      :pageSize="pageSize"
      :page="page"
      :permissions="permissions"
      :ignoreZero="ignoreZero"
      :show-actions="showActions"
      :uid="uid"
      :title="title"
      :allow-edit="props.allowEdit"
      :showHeader="showHeader"
      :refreshData="reloadData"
      :titleField="titleField"
      :rows="props.data"
      @dataLoaded="handleDataLoaded"
      @lists="handleLists"
      :allow-disable="allowDisable"
      :show-quick-view="showQuickView"
      :showShareActions="showShareActions"
      :remote="remote"
      :actions="props.actions"
      :endPoint="endPoint"
      :custom-url="customUrl"
      :showPagination="showPagination"
      :show-child-row="showChildRow"
      :child-row-fields="childRowFields"
      :child-row-title="childRowTitle"
      :child-row-table-width="childRowTableWidth"
      :child-row-data-field="childRowDataField"
      :table-width="tableWidth"
      :require-params="props.requireParams"
      :params="props.params"
      @action="handleTableAction"
      @create="handleCreate"
      @total-records="handleTotalRecords"
    >
    <template #list-table-header-actions>
      <slot name="table-header-actions"></slot>
    </template>
  </DataTable>

    <slot name="content"></slot>
    </div>
    
    <div id="details-panel">

      <ListDetailPanel
        v-if="showDetailPanel && !noPermission" 
        :tabs="tabList" 
        :permissions="permissions"
        :reload="reloadTab"
        :record="selectedData">
      </ListDetailPanel>
      
    <slot name="form-view" v-if="!noPermission" >

    </slot>
    </div>
  </div>
</template>